import { useEffect, useMemo, useState } from "react";
import { ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle, ShieldCheck, Sparkles, X, Undo2, History } from "lucide-react";
import {
  PRESETS,
  FLAVOR_META,
  FLAVOR_TO_PRESET,
  applyState,
  runA11yChecks,
  popHistory,
  loadHistory,
  loadLastSaved,
  type WizardFlavor,
  type StoredState,
} from "@/lib/typography";

type Step = 0 | 1 | 2;

type Props = {
  open: boolean;
  initialState: StoredState;
  onClose: () => void;
  onApply: (next: StoredState) => void;
};

export function TypographyWizard({ open, initialState, onClose, onApply }: Props) {
  const [step, setStep] = useState<Step>(0);
  const [flavor, setFlavor] = useState<WizardFlavor>("balanced");
  const [snapshotBefore] = useState<StoredState>(initialState);

  // Live-preview the chosen flavor while the wizard is open
  const previewState: StoredState = useMemo(
    () => ({ preset: FLAVOR_TO_PRESET[flavor] }),
    [flavor],
  );

  useEffect(() => {
    if (open && step >= 1) applyState(previewState);
  }, [open, step, previewState]);

  // Restore previous state if the user cancels or closes mid-flow
  useEffect(() => {
    if (!open) applyState(snapshotBefore);
  }, [open, snapshotBefore]);

  const a11y = useMemo(() => (step === 2 ? runA11yChecks() : []), [step, previewState]);
  const errorCount = a11y.filter((i) => i.severity === "error").length;
  const warnCount = a11y.filter((i) => i.severity === "warn").length;

  if (!open) return null;

  const presetMeta = PRESETS.find((p) => p.name === FLAVOR_TO_PRESET[flavor]);

  const cancel = () => {
    applyState(snapshotBefore);
    setStep(0);
    onClose();
  };

  const apply = () => {
    onApply(previewState);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[hsl(240_8%_8%/0.97)] shadow-2xl text-white/85 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(268_90%_75%)]" />
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">
              Typography Wizard
            </div>
          </div>
          <button onClick={cancel} className="text-white/50 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex gap-1 px-4 pt-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded-full transition ${
                step >= i ? "bg-[hsl(268_90%_65%)]" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        <div className="p-4 space-y-3 min-h-[260px]">
          {step === 0 && (
            <>
              <div>
                <h3 className="text-sm font-medium">Pick a flavor</h3>
                <p className="text-[11px] text-white/55 mt-0.5">
                  We&apos;ll recommend a preset based on your preference.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {(Object.keys(FLAVOR_META) as WizardFlavor[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFlavor(f)}
                    className={`text-left rounded-lg border px-3 py-2 transition ${
                      flavor === f
                        ? "border-[hsl(268_90%_65%/0.45)] bg-[hsl(268_90%_65%/0.12)]"
                        : "border-white/8 bg-white/[0.02] hover:bg-white/[0.06]"
                    }`}
                  >
                    <div className="text-xs font-medium">{FLAVOR_META[f].label}</div>
                    <div className="text-[10px] text-white/55 mt-0.5">{FLAVOR_META[f].description}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <h3 className="text-sm font-medium">Recommended preset</h3>
                <p className="text-[11px] text-white/55 mt-0.5">
                  Live-previewed in the sidebar. Continue or go back to change.
                </p>
              </div>
              <div className="rounded-lg border border-[hsl(268_90%_65%/0.35)] bg-[hsl(268_90%_65%/0.08)] p-3">
                <div className="text-xs font-semibold text-white">{presetMeta?.label}</div>
                <div className="text-[10px] text-white/55 mt-1 font-mono leading-relaxed">
                  label-font: {presetMeta?.vars["--font-banner-label"]?.replace("var(--font-", "").replace(")", "")}
                  <br />
                  item-font: {presetMeta?.vars["--font-banner-item"]?.replace("var(--font-", "").replace(")", "")}
                  <br />
                  label-size: {presetMeta?.vars["--banner-label-size"]}
                  <br />
                  item-size: {presetMeta?.vars["--banner-item-size"]}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <h3 className="text-sm font-medium">Accessibility safety check</h3>
              </div>
              <p className="text-[11px] text-white/55">
                {errorCount === 0 && warnCount === 0
                  ? "All checks passed. Safe to apply."
                  : `${errorCount} error(s), ${warnCount} warning(s) detected.`}
              </p>
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                {a11y.map((iss) => {
                  const Icon = iss.severity === "error" || iss.severity === "warn" ? AlertTriangle : CheckCircle2;
                  const color =
                    iss.severity === "error" ? "text-rose-400" :
                    iss.severity === "warn" ? "text-amber-400" : "text-emerald-400";
                  return (
                    <div key={iss.id} className="rounded-md border border-white/8 bg-white/[0.02] p-2">
                      <div className={`flex items-start gap-2 text-[11px] ${color}`}>
                        <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <div className="flex-1 text-white/80">
                          <div>{iss.message}</div>
                          {iss.suggestion && <div className="text-white/50 mt-0.5">{iss.suggestion}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* History controls */}
        <div className="flex items-center gap-1.5 px-4 py-2 border-t border-white/8 bg-white/[0.02]">
          <button
            onClick={() => {
              popHistory();
              const list = loadHistory();
              const prev = list[list.length - 1];
              if (prev) { applyState(prev.state); onApply(prev.state); }
            }}
            className="flex-1 flex items-center justify-center gap-1 text-[10px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white/75 px-2 py-1.5"
          >
            <Undo2 className="h-3 w-3" /> Undo last change
          </button>
          <button
            onClick={() => {
              const last = loadLastSaved();
              if (!last) return;
              applyState(last);
              onApply(last);
            }}
            className="flex-1 flex items-center justify-center gap-1 text-[10px] rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white/75 px-2 py-1.5"
          >
            <History className="h-3 w-3" /> Restore last saved
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/8 bg-white/[0.02]">
          <button
            onClick={() => (step === 0 ? cancel() : setStep((s) => (s - 1) as Step))}
            className="flex items-center gap-1 text-[11px] text-white/60 hover:text-white px-2 py-1.5"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < 2 ? (
            <button
              onClick={() => setStep((s) => (s + 1) as Step)}
              className="flex items-center gap-1 text-[11px] rounded-lg bg-[hsl(268_90%_65%)] hover:bg-[hsl(268_90%_70%)] text-white px-3 py-1.5 font-medium"
            >
              Continue
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              onClick={apply}
              disabled={errorCount > 0}
              className="flex items-center gap-1 text-[11px] rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-white px-3 py-1.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              title={errorCount > 0 ? "Fix errors before applying" : "Apply preset"}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Apply preset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
